import moment from "moment";
import * as React from "react";
import * as calendarTaskService from "../../models/services/calendarTaskService";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { customHistory } from "../../framework/customHistory";
import { ICalendarTaskViewState as IStoreCalendarTaskViewState, IUserState } from "../../models/store/storeTypes";
import { SubPageHeader } from "../app/header";
import { ActivePageType, NavigationBar } from "../app/navigation";
import { CalendarTaskListLine } from "./calendarTaskListLine";
import { CalendarTaskViewMenu } from "./calendarTaskViewMenu";
import { SelectDayDialog } from "../framework/selectDayDialog";
import { AppUtils } from "source/framework/appUtils";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { CalendarTaskViewSearchDialog } from "./calendarTaskViewSearchDialog";
import { ICalendarTaskPermissions } from "ui/Scripts/source/models/calendarTask/calendarTaskPermissions";

//CalendarTaskView
export interface ICalendarTaskViewOwnProps {
}

export interface ICalendarTaskViewStateProps {
    user: IUserState;
    calendarTaskView: IStoreCalendarTaskViewState;
    lastOpenedCalendarTaskId: string;
}

export interface ICalendarTaskViewDispatchProps {
    loadCalendarTasks: (startDate: number, endDate: number, filter: string, stateId: string, categoryId: string) => void;
    removeCalendarTask: (id: string) => void;
    resetCalendarTasks: () => void;
    setLastOpenedCalendarTaskId: (id: string) => void;
    setReturnUrl: (url: string) => void;
    onLogout: () => void;
}

type CalendarTaskViewProp = ICalendarTaskViewOwnProps & ICalendarTaskViewStateProps & ICalendarTaskViewDispatchProps;

interface ICalendarTaskViewState {
    selectedId: string;
    showViewMenu: boolean;
    viewMenuPermissions: ICalendarTaskPermissions;
    showSelectDayDialog: boolean;
    showSearchDialog: boolean;
    listMenuItem: ICalendarTaskItem;
}

const getEventsByDay = (events: ICalendarTaskItem[]) => {
    const result: ICalendarTaskItem[] = [];

    events.forEach(event => {
        result.push({ ...event });
        if (event.endDate && Base.timeToDateStr(event.endDate) !== Base.timeToDateStr(event.startDate)) {
            let mDate = moment(event.startDate);
            const mEndDate = moment(event.endDate);
            
            while (!mDate.isSame(mEndDate, "date")) {
                mDate.add(1, "days");
                if (mDate.isSame(mEndDate, "date")) {
                    mDate = mEndDate;
                }
                if (!mDate.isSame(mEndDate) || Base.timeToTimeStr(event.endDate) !== "0:00") {
                    result.push({
                        ...event,
                        startDate: parseInt(mDate.format("x")),
                        allDay: event.allDay || !mDate.isSame(mEndDate, "date"),
                    });
                }
            }
        }
        return [...result];
    });

    return result;
};

export interface ICalendarTaskDayItem {
    id: string;
    dayNumber: number;
    dayLabel: string;
    events: ICalendarTaskItem[];
}

const getEventsByWeek = (startDate: number, events: ICalendarTaskItem[]) => {
    const result: ICalendarTaskDayItem[] = [];
    const currentDate = moment(startDate).startOf("week").startOf("day");
    const nextWeek = currentDate.clone().add(1, "week");
    while (currentDate.isBefore(nextWeek)) {
        result.push({
            id: `day-${currentDate.format("D")}`,
            dayNumber: currentDate.day(),
            dayLabel: `${currentDate.format("dddd")} ${currentDate.format("D.M.YYYY")}`,
            events: events.filter(event => moment(event.startDate).isSame(currentDate, "day"))
        });
        currentDate.add(1, "days");
    }

    return result;
};

export class CalendarTaskView extends React.Component<CalendarTaskViewProp, ICalendarTaskViewState> {
    private hasBeenInitialized: boolean;
    private containerDiv: HTMLDivElement;

    constructor(props) {
        super(props);
        this.hasBeenInitialized = false;
        this.state = {
            selectedId: null,
            showViewMenu: false,
            viewMenuPermissions: null,
            showSearchDialog: false,
            listMenuItem: null,
            showSelectDayDialog: false
        };
    }

    getFilter = (): string => {
        return this.props.calendarTaskView?.filter ?? "";
    };

    getStateId = (): string => {
        return this.props.calendarTaskView?.stateId ?? "-1";
    };

    getCategoryId = (): string => {
        return this.props.calendarTaskView?.categoryId ?? Base.emptyGuid;
    };

    componentDidMount(): void {
        this.props.loadCalendarTasks(this.props.calendarTaskView?.items?.startDate, this.props.calendarTaskView?.items?.endDate, this.getFilter(), this.getStateId(), this.getCategoryId());
        //Scroll last opened item to view
        if (this.props.calendarTaskView?.items?.items?.length > 0 && !Base.isNullOrEmpty(this.props.calendarTaskView?.lastOpenedCalendarTasklId)) {
            if (this.containerDiv.scrollTo) {
                const index = this.props.calendarTaskView?.items?.items?.findIndex(i => i.id === this.props.calendarTaskView?.lastOpenedCalendarTasklId);
                if (index > -1) {
                    const elements = this.containerDiv.getElementsByClassName("loe");
                    if (elements.length > 0 && !Base.isNullOrUndefined(elements[0])) {
                        const y = (elements[0] as HTMLDivElement).offsetTop;
                        this.containerDiv.scrollTo({
                            top: y,
                            left: 0,
                            behavior: "auto"
                        });
                    }
                }
            }
            this.props.setLastOpenedCalendarTaskId("");
        } else {
            if (this.containerDiv.scrollTo) {
                const elements = this.containerDiv.getElementsByClassName("today");
                console.log("today elements", elements);
                if (elements.length > 0 && !Base.isNullOrUndefined(elements[0])) {
                    const y = (elements[0] as HTMLDivElement).offsetTop;
                    this.containerDiv.scrollTo({
                        top: y,
                        left: 0,
                        behavior: "auto"
                    });
                }
            }
        }
        this.hasBeenInitialized = true;
    }

    removeCalendarTask = async (item: ICalendarTaskItem) => {
        if (!item) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCalendarTaskParameter, item.title), false) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => calendarTaskService.removeCalendarTask(item.id));
            if (!result) return;
        }
        this.props.loadCalendarTasks(this.props.calendarTaskView?.items?.startDate, this.props.calendarTaskView?.items?.endDate, this.getFilter(), this.getStateId(), this.getCategoryId());
    };

    getViewTitleSuffix = () => {
        return this.getFilter();
    };

    handleFilterCalendarTasks = () => {
        this.setState({
            showSearchDialog: true
        });
    };

    handleSearchDialogOk = (filter: string, stateId: string, categoryId: string) => {
        this.setState({
            showSearchDialog: false
        });
        this.props.loadCalendarTasks(this.props.calendarTaskView?.items?.startDate, this.props.calendarTaskView?.items?.endDate, filter, stateId, categoryId);
    };

    handleSearchDialogCancel = () => {
        this.setState({
            showSearchDialog: false
        });
    };

    setReturnUrl = () => {
        this.props.setReturnUrl("/calendartask");
    };

    handleLineClick = (id: string) => {
        this.props.setLastOpenedCalendarTaskId(id);
        this.setReturnUrl();
        customHistory.push("/calendartask/" + id);
    };

    handleAddClick = () => {
        this.props.setLastOpenedCalendarTaskId("");
        this.setReturnUrl();
        customHistory.push("/calendartask/" + Base.emptyGuid);
    };

    handleMenuEdit = (id: string) => {
        this.props.setLastOpenedCalendarTaskId(id);
        this.setReturnUrl();
        customHistory.push("/calendartask/" + id);
        this.setState({
            showViewMenu: false
        });
    };

    handleMenuRemove = (id: string) => {
        this.setState({
            showViewMenu: false
        });
        this.removeCalendarTask(this.state.listMenuItem);
    };

    getStartDate = (): number => {
        return this.props.calendarTaskView?.items?.startDate ?? Base.getFirstDayOfWeek(new Date()).getTime();
    };

    handlePreviousClick = () => {
        const startDate = new Date(this.getStartDate()).addDays(-7);
        const endDate = startDate.addDays(7);
        this.props.loadCalendarTasks(startDate.getTime(), endDate.getTime(), this.getFilter(), this.getStateId(), this.getCategoryId());
    };

    handleNextClick = () => {
        const startDate = new Date(this.getStartDate()).addDays(7);
        const endDate = startDate.addDays(7);
        this.props.loadCalendarTasks(startDate.getTime(), endDate.getTime(), this.getFilter(), this.getStateId(), this.getCategoryId());
    };

    handleShowSelectDayDialog = () => {
        this.setState({
            showSelectDayDialog: true
        });
    };

    handleSelectDayDialogOk = (value: string) => {
        this.setState({
            showSelectDayDialog: false
        });
        const startDate = Base.getFirstDayOfWeek(value.toDate());
        const endDate = startDate.addDays(7);
        this.props.loadCalendarTasks(startDate.getTime(), endDate.getTime(), this.getFilter(), this.getStateId(), this.getCategoryId());
    };

    handleSelectDayDialogCancel = () => {
        this.setState({
            showSelectDayDialog: false
        });
    };

    handleLineLongPress = async (id: string) => {
        const listMenuItem = this.props.calendarTaskView?.items?.items?.find(i => i.id === id);
        if (Base.isNullOrUndefined(listMenuItem)) return;
        const permissions = await AppUtils.callService(() => calendarTaskService.getCalendarTaskPermissions(id, listMenuItem.recordId, listMenuItem.databaseRecordType), false);
        if (!permissions) return;
        this.setState({
            selectedId: id,
            showViewMenu: true,
            viewMenuPermissions: permissions,
            listMenuItem,
        });
    };

    handleRefreshList = () => {
        this.props.loadCalendarTasks(this.props.calendarTaskView?.items?.startDate, this.props.calendarTaskView?.items?.endDate, this.getFilter(), this.getStateId(), this.getCategoryId());
    };

    handleMenuCancel = () => {
        this.setState({
            showViewMenu: false,
        });
    };

    //#region swipe
    private startX: number = 0;
    private startY: number = 0;

    handleLinePress = (ev) => {
        //Get coordinates
        let x: number;
        let y: number;
        if (ev.targetTouches) {
            // Prefer Touch Events
            x = ev.targetTouches[0].clientX;
            y = ev.targetTouches[0].clientY;
        } else {
            // Either Mouse event or Pointer Event
            x = ev.clientX;
            y = ev.clientY;
        }
        this.startX = x;
        this.startY = y;
    };

    handleLineRelease = (ev) => {
        if (this.startX === 0) return;
        //Calculate finger movement
        let lastX = 0;
        let lastY = 0;
        if (ev.changedTouches && ev.changedTouches.length > 0) {
            lastX = ev.changedTouches[ev.changedTouches.length - 1].clientX;
            lastY = ev.changedTouches[ev.changedTouches.length - 1].clientY;
        } else if (ev.clientX) {
            lastX = ev.clientX;
            lastY = ev.clientY;
        }
        //Check for swipe
        if (lastX > 0) {
            const xDiff = this.startX - lastX;
            const yDiff = this.startY - lastY;
            if (Math.abs(xDiff) > 150 && Math.abs(yDiff) < 75) {
                if (xDiff > 0) { //finger moved left
                    this.handleNextClick();
                } else if (xDiff < 0) { //finger moved right
                    this.handlePreviousClick();
                }
            }
        }
        this.startX = 0;
    };
    //#endregion

    render() {
        const props = this.props;
        const state = this.state;
        const eventsByDay = getEventsByDay(props.calendarTaskView?.items?.items || []);
        const eventsByWeekDay = getEventsByWeek(this.getStartDate(), eventsByDay);
        const todaysDate = Base.timeToDateStr(parseInt(moment().format("x")));
        
        return (
            <div>
                <div className="pageContentContainer">
                    <div className="calendarTaskViewContainer mainListContainer" ref={(elem) => { this.containerDiv = elem; }}>
                        <div className="lineContainer mainLineContainer calendarTaskView">
                            <div className="line dataTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "25px" }}>
                                <ToolButton
                                    classes="round mono previous white left"
                                    stopPropagation={true}
                                    onClick={this.handlePreviousClick}
                                />
                                <span onClick={this.handleShowSelectDayDialog}>
                                    {Translations.Week + " " + moment(props.calendarTaskView?.items?.startDate || undefined).format("W")}
                                </span>
                                <ToolButton
                                    classes="round mono next white"
                                    stopPropagation={true}
                                    onClick={this.handleNextClick}
                                />
                            </div>
                            {/* {this.hasBeenInitialized && !props.calendarTaskView?.isLoading && props.calendarTaskView?.items.items.length < 1 &&
                                <div className="infoContainer">
                                    <div className="alert alert-info" role="alert" dangerouslySetInnerHTML={ { __html: Translations.NoCalendarTasksToShow } } />
                                </div>
                            } */}
                            <div className="calendarTaskView"
                                onTouchStart={this.handleLinePress}
                                onTouchEnd={this.handleLineRelease}
                                onMouseDown={this.handleLinePress}
                                onMouseUp={this.handleLineRelease}
                                onMouseLeave={this.handleLineRelease}>
                                {eventsByWeekDay.map((day) => (
                                    <div key={day.id} className="day">
                                        <div className="line dataTitle">
                                            <span className={day.dayLabel.includes(todaysDate) ? "today" : null} style={{ color: day.dayLabel.includes(todaysDate) ? "#2856b7" : null }}>{day.dayLabel}</span>
                                        </div>
                                        <div className="dayContent">
                                            {day.events?.length > 0 && day.events.map((event) => (
                                                <CalendarTaskListLine
                                                    item={event}
                                                    first={false}
                                                    isLastOpenedEvent={event.id === props.calendarTaskView?.lastOpenedCalendarTasklId}
                                                    key={event.id}
                                                    selected={false}
                                                    hideDate
                                                    onClick={this.handleLineClick}
                                                    onLongPress={this.handleLineLongPress}
                                                    onRefreshList={this.handleRefreshList}
                                                    onMoveToNextWeek={this.handleNextClick}
                                                    onMoveToPreviousWeek={this.handlePreviousClick}
                                                />
                                            ))}
                                            {day.events?.length === 0 &&
                                                <div style={{ padding: "20px", fontStyle: "italic", textAlign: "center" }}>{Translations.NoCalendarTasksToShow}</div>
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <NavigationBar
                    activePage={ActivePageType.CalendarTask}
                    user={props.user}
                />
                <SubPageHeader
                    user={props.user}
                    title={Translations.Calendar + this.getViewTitleSuffix()}
                    searchIsSet={!!this.getFilter() || this.getStateId() !== "-1" || this.getCategoryId() !== Base.emptyGuid}
                    onSearch={this.handleFilterCalendarTasks}
                    onAdd={this.handleAddClick}
                />
                {state.showViewMenu &&
                    <CalendarTaskViewMenu
                        item={state.listMenuItem}
                        permissions={state.viewMenuPermissions}
                        onEdit={this.handleMenuEdit}
                        onRemove={this.handleMenuRemove}
                        onCancel={this.handleMenuCancel}
                    />
                }
                {state.showSelectDayDialog &&
                    <SelectDayDialog
                        defaultValue={Base.utcTimeToDateStr(Base.getNowUtcDate().getTime())}
                        onOk={this.handleSelectDayDialogOk}
                        onCancel={this.handleSelectDayDialogCancel}
                    />
                }
                {state.showSearchDialog &&
                    <CalendarTaskViewSearchDialog
                        // stateId={props.stateId}
                        categories={props.calendarTaskView?.items.categories ?? []}
                        categoryId={this.getCategoryId()}
                        stateId={this.getStateId()}
                        filter={this.getFilter()}
                        onOk={this.handleSearchDialogOk}
                        onCancel={this.handleSearchDialogCancel}
                    />
                }
            </div>
        );
    }
}